import { useTranslations } from "@/lib/useTranslations";
import { startTransition, useActionState, useCallback, useEffect, useMemo, useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { isArticleContentDirty } from "@/lib/content-dirty";
import { getLibraryReturnHref } from "@/lib/library-search-snapshot";

import {
  cancelArticleCreationAction,
  deleteArticleSignalAction,
  updateArticleAssignment,
  updateArticleContent,
  updateArticleCoverImage,
  updateArticleInternalNotes,
  updateArticleStatus,
  uploadArticleCoverImage,
} from "@/api/articles";
import { Button } from "@/components/ui/Button";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { ArticleAssigneeField } from "@/components/ui/admin/articles/ArticleAssigneeField";
import { ArticleCoverImageField } from "@/components/ui/admin/articles/ArticleCoverImageField";
import { ArticleEditorialCycleCard } from "@/components/ui/admin/articles/ArticleEditorialCycleCard";
import { ArticleRichTextEditor } from "@/components/ui/admin/articles/ArticleRichTextEditor";
import { ArticleEditorialChecklist } from "@/components/ui/admin/articles/ArticleEditorialChecklist";
import { ArticleSuggestionsPanel } from "@/components/ui/admin/articles/ArticleSuggestionsPanel";
import { ArticleTaxonomyPanel } from "@/components/ui/admin/articles/ArticleTaxonomyPanel";
import { InternalNotesField } from "@/components/ui/admin/InternalNotesField";
import { ShareLinkButton } from "@/components/ui/admin/ShareLinkButton";
import type {
  ArticleAssigneeOption,
  ArticleEditStatusValue,
  ArticleEditor,
  ArticleStatusAction,
  ArticleSuggestions,
  ArticleTaxonomy,
  UpdateArticleState,
} from "@/core/services/types/article.types";
import {
  resolveArticleEditStatus,
  isArticleEditorialChecklistComplete,
} from "@/core/services/types/article.types";
import type {
  ArtForm,
  ArtisticPeriod,
  CulturalArea,
  ExhibitionType,
} from "@/core/services/types/taxonomy.types";

const initialUpdateArticleState: UpdateArticleState = { success: false };

type ArticleEditQueryData = {
  article: ArticleEditor;
  teamMembers: ArticleAssigneeOption[];
  suggestions: ArticleSuggestions;
  articleTaxonomy: ArticleTaxonomy;
  taxonomyData: {
    artForms: ArtForm[];
    artisticPeriods: ArtisticPeriod[];
    culturalAreas: CulturalArea[];
    exhibitionTypes: ExhibitionType[];
  };
};

type ArticleEditTab = "content" | "suggestions";

const titleInputClassName =
  "w-full wireframe-border bg-whart-bg px-4 py-3 text-3xl font-bold text-whart-text outline-none placeholder:text-neutral-300 focus:border-whart-dark disabled:opacity-50";

const textareaClassName =
  "w-full resize-y wireframe-border bg-whart-bg px-4 py-3 text-sm leading-relaxed text-whart-text outline-none focus:border-whart-dark disabled:opacity-50";

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-whart-urgent">{message}</p>;
}

function SaveSpinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-zinc-900 border-r-transparent"
    />
  );
}

interface ArticleEditFormProps {
  article: ArticleEditor;
  assignableMembers: ArticleAssigneeOption[];
  shareUrl: string;
  canCancelCreation: boolean;
  suggestions: ArticleSuggestions;
  taxonomy: ArticleTaxonomy;
  artForms: ArtForm[];
  artisticPeriods: ArtisticPeriod[];
  culturalAreas: CulturalArea[];
  exhibitionTypes: ExhibitionType[];
}

export function ArticleEditForm({
  article,
  assignableMembers,
  shareUrl,
  canCancelCreation,
  suggestions,
  taxonomy,
  artForms,
  artisticPeriods,
  culturalAreas,
  exhibitionTypes,
}: ArticleEditFormProps) {
  const t = useTranslations("articles.edit");
  const tCommon = useTranslations("common");
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<ArticleEditTab>("content");
  const [isCancelPending, startCancelTransition] = useTransition();
  const [contentState, contentFormAction, isContentPending] = useActionState(
    updateArticleContent,
    initialUpdateArticleState,
  );
  const [statusState, statusFormAction, isStatusPending] = useActionState(
    updateArticleStatus,
    initialUpdateArticleState,
  );
  const [assignmentState, assignmentAction, isAssignmentPending] =
    useActionState(updateArticleAssignment, initialUpdateArticleState);
  const [internalNotesState, internalNotesAction, isInternalNotesPending] =
    useActionState(updateArticleInternalNotes, initialUpdateArticleState);
  const [coverImageState, coverImageAction, isCoverImagePending] =
    useActionState(updateArticleCoverImage, initialUpdateArticleState);
  const [coverImageUploadState, coverImageUploadAction, isCoverImageUploadPending] =
    useActionState(uploadArticleCoverImage, initialUpdateArticleState);
  const [assignedTo, setAssignedTo] = useState(article.assigned_to ?? "");
  const [pendingStatusTarget, setPendingStatusTarget] =
    useState<ArticleEditStatusValue | null>(null);
  const [title, setTitle] = useState(article.title);
  const [subTitle, setSubTitle] = useState(article.sub_title ?? "");
  const [contentHtml, setContentHtml] = useState(
    () => article.content_rich_text ?? "",
  );
  const [internalNotes, setInternalNotes] = useState(
    () => article.internal_notes ?? "",
  );

  const patchArticleCache = useCallback(
    (updatedArticle: ArticleEditor) => {
      queryClient.setQueryData<ArticleEditQueryData>(
        ["article-edit", article.id],
        (current) => {
          if (!current) return current;
          return { ...current, article: updatedArticle };
        },
      );
    },
    [article.id, queryClient],
  );

  const savedStatus = resolveArticleEditStatus(article);
  const isEditorialChecklistComplete = isArticleEditorialChecklistComplete(article);
  const hasUnsavedContentChange = useMemo(
    () =>
      isArticleContentDirty(article, {
        title,
        subTitle,
        contentHtml,
      }),
    [article, title, subTitle, contentHtml],
  );
  const contentFieldErrors = contentState.success
    ? undefined
    : contentState.fieldErrors;
  const assignmentFieldErrors = assignmentState.success
    ? undefined
    : assignmentState.fieldErrors;
  const coverImageFieldErrors = coverImageState.success
    ? undefined
    : coverImageState.fieldErrors;
  const isCoverImageBusy = isCoverImagePending || isCoverImageUploadPending;
  const coverImageError = coverImageUploadState.success
    ? undefined
    : coverImageUploadState.error ?? (coverImageState.success ? undefined : coverImageState.error);
  const coverImageSuccess =
    coverImageUploadState.success || coverImageState.success;

  useEffect(() => {
    if (!isStatusPending) {
      setPendingStatusTarget(null);
    }
  }, [isStatusPending]);

  useEffect(() => {
    if (!contentState.success) return;
    patchArticleCache(contentState.data);
    setTitle(contentState.data.title);
    setSubTitle(contentState.data.sub_title ?? "");
    setContentHtml(contentState.data.content_rich_text ?? "");
  }, [contentState.success, contentState, patchArticleCache]);

  useEffect(() => {
    if (!statusState.success) return;
    patchArticleCache(statusState.data);
  }, [statusState.success, statusState, patchArticleCache]);

  useEffect(() => {
    if (!assignmentState.success) return;
    patchArticleCache(assignmentState.data);
    setAssignedTo(assignmentState.data.assigned_to ?? "");
  }, [assignmentState.success, assignmentState, patchArticleCache]);

  useEffect(() => {
    if (!internalNotesState.success) return;
    patchArticleCache(internalNotesState.data);
    setInternalNotes(internalNotesState.data.internal_notes ?? "");
  }, [internalNotesState.success, internalNotesState, patchArticleCache]);

  useEffect(() => {
    if (!coverImageState.success) return;
    patchArticleCache(coverImageState.data);
  }, [coverImageState.success, coverImageState, patchArticleCache]);

  useEffect(() => {
    if (!coverImageUploadState.success) return;
    patchArticleCache(coverImageUploadState.data);
  }, [coverImageUploadState.success, coverImageUploadState, patchArticleCache]);

  function submitStatusUpdate(
    targetStatus: ArticleEditStatusValue,
    options?: { publishImmediately?: boolean },
  ) {
    const formData = new FormData();
    formData.set("id", article.id);
    formData.set("edit_status", targetStatus);
    if (options?.publishImmediately) {
      formData.set("publish_immediately", "true");
    }
    startTransition(() => {
      statusFormAction(formData);
    });
  }

  function handleStatusAction(action: ArticleStatusAction) {
    if (!action.target) return;
    setPendingStatusTarget(action.target);
    submitStatusUpdate(action.target, {
      publishImmediately: action.labelKey === "publishNow",
    });
  }

  function handleScheduleSuccess(updatedArticle: ArticleEditor) {
    patchArticleCache(updatedArticle);
  }

  function handleAssigneeChange(value: string) {
    setAssignedTo(value);

    const formData = new FormData();
    formData.set("id", article.id);
    formData.set("assigned_to", value);
    startTransition(() => {
      assignmentAction(formData);
    });
  }

  function handleInternalNotesBlur() {
    const savedNotes = article.internal_notes ?? "";
    if (internalNotes === savedNotes) return;

    const formData = new FormData();
    formData.set("id", article.id);
    formData.set("internal_notes", internalNotes);
    startTransition(() => {
      internalNotesAction(formData);
    });
  }

  function submitCoverImageRemoval() {
    const formData = new FormData();
    formData.set("id", article.id);
    formData.set("cover_image_url", "");
    startTransition(() => {
      coverImageAction(formData);
    });
  }

  function handleCoverImageUpload(file: File) {
    const formData = new FormData();
    formData.set("id", article.id);
    formData.set("cover_image", file);
    startTransition(() => {
      coverImageUploadAction(formData);
    });
  }

  function handleCancelCreation() {
    startCancelTransition(() => {
      void cancelArticleCreationAction(article.id);
    });
  }

  async function handleDeleteSignal() {
    await deleteArticleSignalAction(article.id);
    navigate(getLibraryReturnHref("articles"));
  }

  const isFormBusy =
    isContentPending ||
    isStatusPending ||
    isAssignmentPending ||
    isCoverImageBusy ||
    isCancelPending;

  return (
    <div className="space-y-6">
      <nav aria-label={t("tabsNav")} className="border-b border-black/10">
        <ul className="flex items-center gap-1">
          <li>
            <button
              type="button"
              onClick={() => setActiveTab("content")}
              className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                activeTab === "content"
                  ? "border-whart-dark font-medium text-whart-text"
                  : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
              }`}
              aria-current={activeTab === "content" ? "page" : undefined}
            >
              {t("tabs.content")}
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={() => setActiveTab("suggestions")}
              className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${
                activeTab === "suggestions"
                  ? "border-whart-dark font-medium text-whart-text"
                  : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
              }`}
              aria-current={activeTab === "suggestions" ? "page" : undefined}
            >
              {t("tabs.suggestions")}
              <span className="ml-1.5 text-neutral-400">
                ({suggestions.places.length + suggestions.events.length})
              </span>
            </button>
          </li>
        </ul>
      </nav>

      {activeTab === "suggestions" ? (
        <ArticleSuggestionsPanel
          articleId={article.id}
          suggestions={suggestions}
        />
      ) : (
    <div className="space-y-8 md:grid md:grid-cols-3 md:gap-8 md:space-y-0">
      <form
        action={contentFormAction}
        className="space-y-8 md:col-span-2"
      >
        <input type="hidden" name="id" value={article.id} />

        <div className="flex flex-col gap-2">
          {!contentState.success && contentState.error ? (
            <p
              role="alert"
              className="w-full wireframe-border px-3 py-2 text-sm text-whart-urgent"
            >
              {contentState.error}
            </p>
          ) : null}

          {contentState.success ? (
            <p
              role="status"
              className="w-full wireframe-border px-3 py-2 text-sm text-emerald-700"
            >
              {t("saveContentSuccess")}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center justify-end gap-2">
            {canCancelCreation ? (
              <Button
                type="button"
                variant="secondary"
                disabled={isFormBusy}
                aria-busy={isCancelPending}
                onClick={handleCancelCreation}
                className="mr-auto inline-flex shrink-0 items-center justify-center gap-2"
              >
                {isCancelPending ? <SaveSpinner /> : null}
                {isCancelPending ? t("cancelling") : t("cancelCreation")}
              </Button>
            ) : null}

            <Button
              type="submit"
              disabled={isContentPending || !hasUnsavedContentChange}
              aria-busy={isContentPending}
              className="inline-flex shrink-0 items-center justify-center gap-2"
            >
              {isContentPending ? <SaveSpinner /> : null}
              {isContentPending ? tCommon("saving") : t("saveContent")}
            </Button>
          </div>
        </div>

        <ArticleCoverImageField
          required
          savedUrl={article.cover_image_url}
          disabled={isCoverImageBusy}
          isPending={isCoverImageBusy}
          saveSpinner={<SaveSpinner />}
          fieldError={coverImageFieldErrors?.cover_image_url}
          error={coverImageError}
          success={coverImageSuccess}
          onUpload={handleCoverImageUpload}
          onRemove={submitCoverImageRemoval}
        />

        <section className="space-y-2">
          <FieldLabel htmlFor="article-title" required>
            {t("titleLabel")}
          </FieldLabel>
          <input
            id="article-title"
            name="title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t("titlePlaceholder")}
            disabled={isContentPending}
            className={titleInputClassName}
          />
          <FieldError message={contentFieldErrors?.title} />
        </section>

        <section className="space-y-2">
          <FieldLabel htmlFor="article-sub-title" required>
            {t("abstractLabel")}
          </FieldLabel>
          <textarea
            id="article-sub-title"
            name="sub_title"
            rows={4}
            value={subTitle}
            onChange={(event) => setSubTitle(event.target.value)}
            placeholder={t("abstractPlaceholder")}
            disabled={isContentPending}
            className={textareaClassName}
          />
          <FieldError message={contentFieldErrors?.sub_title} />
        </section>

        <section className="space-y-2" aria-labelledby="article-content-label">
          <FieldLabel id="article-content-label" as="p" required>
            {t("contentLabel")}
          </FieldLabel>
          <ArticleRichTextEditor
            editorKey={article.id}
            value={contentHtml}
            placeholder={t("contentPlaceholder")}
            disabled={isContentPending}
            labels={{
              toolbar: t("editor.toolbar"),
              bold: t("editor.bold"),
              italic: t("editor.italic"),
              underline: t("editor.underline"),
              heading2: t("editor.heading2"),
              heading3: t("editor.heading3"),
              bulletList: t("editor.bulletList"),
              orderedList: t("editor.orderedList"),
              blockquote: t("editor.blockquote"),
            }}
            onChange={setContentHtml}
          />
          <input type="hidden" name="content_rich_text" value={contentHtml} />
          <FieldError message={contentFieldErrors?.content_rich_text} />
        </section>
      </form>

      <aside className="mt-8 space-y-4 md:col-span-1 md:mt-0">
        <div className="sticky top-6 space-y-4">
          <ArticleEditorialChecklist article={article} />

          <div className="wireframe-border bg-white p-5">
            <ArticleEditorialCycleCard
              articleId={article.id}
              publishedAt={article.published_at}
              savedStatus={savedStatus}
              itemLabel={article.title}
              isStatusPending={isStatusPending}
              pendingStatusTarget={pendingStatusTarget}
              canPublish={isAdmin}
              canDelete={isAdmin}
              publishDisabled={!isEditorialChecklistComplete}
              saveSpinner={<SaveSpinner />}
              onStatusAction={handleStatusAction}
              onScheduleSuccess={handleScheduleSuccess}
              onDeleteSignal={handleDeleteSignal}
            />
          </div>

          {!statusState.success && statusState.error ? (
            <p
              role="alert"
              className="wireframe-border bg-white px-5 py-3 text-sm text-whart-urgent"
            >
              {statusState.error}
            </p>
          ) : null}

          {statusState.success ? (
            <p
              role="status"
              className="wireframe-border bg-white px-5 py-3 text-sm text-emerald-700"
            >
              {t("saveStatusSuccess")}
            </p>
          ) : null}
        </div>

        <ArticleAssigneeField
          members={assignableMembers}
          value={assignedTo}
          disabled={isAssignmentPending}
          isPending={isAssignmentPending}
          saveSpinner={<SaveSpinner />}
          fieldError={assignmentFieldErrors?.assigned_to}
          error={assignmentState.success ? undefined : assignmentState.error}
          success={assignmentState.success}
          onChange={handleAssigneeChange}
        />

        <ArticleTaxonomyPanel
          articleId={article.id}
          taxonomy={taxonomy}
          artForms={artForms}
          artisticPeriods={artisticPeriods}
          culturalAreas={culturalAreas}
          exhibitionTypes={exhibitionTypes}
        />

        <ShareLinkButton shareUrl={shareUrl} />

        <InternalNotesField
          entityId={article.id}
          value={internalNotes}
          disabled={isFormBusy}
          isPending={isInternalNotesPending}
          saveSpinner={<SaveSpinner />}
          error={
            internalNotesState.success ? undefined : internalNotesState.error
          }
          success={internalNotesState.success}
          onChange={setInternalNotes}
          onBlur={handleInternalNotesBlur}
        />
      </aside>
    </div>
      )}
    </div>
  );
}
